import {
  MacronutrientDistributionTypeEnum
} from '../macronutrient-distribution-strategy/enums/macronutrient-distribution-type.enum';
import {MacronutrientDistribution} from '../macronutrient-distribution-strategy/macronutrient-distribution';
import {_DEFAULT_NUTRITIONAL_PROPERTIES, IFoodGroupIngredient, INutritionalProperties} from '../../models/food-group';
import {BlockPeriodClass} from './block-period.class';
import {cloneDeep} from 'lodash';
import {IBlockPeriod, IPlanPeriod} from '../../models/meal-plans';
import {BlockPeriodDTO} from '../../dtos/meal-plan/block-period.dto';
import {IIngredient} from '../../models/ingredient';
import {_MICRONUTRIENTS, IMicronutrients} from '../../components/micronutrient-list/micronutrient-list.component';
import { AnyObject } from 'immer/dist/internal';
import { MealTimeClass } from './meal-time.class';
import { IMealTime } from 'src/app/models/meal-plans/meal-time.interface';
import { MealTimeDTO } from 'src/app/dtos/meal-plan/meal-time.dto';

export class PlanPeriodClass {
  name: string;
  nutritionalProperties?: INutritionalProperties;
  blockPeriods?: BlockPeriodClass[];
  changeName: boolean;
  microNutrients: IMicronutrients[];
  macroNutrients: any[];
  addNewBlock: boolean;
  modeReadOnly?: boolean;
  metadata: AnyObject = {
    distribution: {},
    nutrientTable: {}
  };
  isEditing?: boolean;
  valid?: boolean;
  mealTimes: MealTimeClass[];

  constructor(planPeriod: IPlanPeriod, blockPeriods: BlockPeriodClass[], mealTimes: MealTimeClass[], changeName = true) {
    this.name = planPeriod?.name ?? 'Nuevo periodo';
    this.blockPeriods = blockPeriods ?? [];
    this.nutritionalProperties = planPeriod?.nutritionalProperties ?? cloneDeep(_DEFAULT_NUTRITIONAL_PROPERTIES);
    this.changeName = changeName;
    this.microNutrients = _MICRONUTRIENTS;
    this.macroNutrients = [
      {
        _id: 'carbohydrate_g',
        name: 'H.C. netos',
        value: 0
      },
      {
        _id: 'dietaryFiber_g',
        name: 'Fibra',
        value: 0
      },
      {
        _id: 'totalFat_g',
        name: 'Grasas',
        value: 0
      },
      {
        _id: 'protein_g',
        name: 'Proteínas',
        value: 0
      }
    ];
    this.modeReadOnly = false;
    this.metadata = planPeriod?.metadata || { distribution: {}, nutrientTable: {} };
    this.mealTimes = mealTimes;
  }

  setChangeName(value: boolean): void {
    this.changeName = value;
  }

  setName(name: string): void {
    this.name = name.length > 0 ? name : 'nuevo periodo';
    this.changeName = false;
  }

  setAddNewBlock(value: boolean): void {
    this.addNewBlock = value;
  }

  createBlockPeriod(blockPeriodClass?: BlockPeriodClass): void {
    const blockPeriod = blockPeriodClass ?? new BlockPeriodClass();
    this.blockPeriods.push(blockPeriod);
  }

  createMealTime(mealTimeClass: MealTimeClass): void {
    this.mealTimes.push(mealTimeClass);
  }

  editBlockPeriod(blockPeriodClass: BlockPeriodClass, index: number): void {
    if (this.blockPeriods[index] && blockPeriodClass._id === this.blockPeriods[index]._id) {
      this.blockPeriods[index] = blockPeriodClass;
    }
  }

  editMealTime(mealTimeClass: MealTimeClass, index: number): void {
    if (this.mealTimes[index] && mealTimeClass._id === this.mealTimes[index]._id) {
      this.mealTimes[index] = mealTimeClass;
    }
  }

  deleteBlockPeriod(index: number): void {
    this.blockPeriods.splice(index, 1);
  }

  deleteMealTime(index: number): void {
    this.mealTimes.splice(index, 1);
  }

  setModeReadOnly(value: boolean): void {
    this.modeReadOnly = value;
    this.blockPeriods.forEach(block => block.setModeReadOnly(value));
  }

  setMacronutrientDistribution(value: MacronutrientDistribution, strategyName: MacronutrientDistributionTypeEnum): void {
    try {
      const macros = Object.keys(value);
      const metadataDistribution = {};
      macros.forEach(macro => {
        metadataDistribution[macro] = {
          block: value[macro].block,
          value: value[macro].value,
          defaultValue: value[macro].defaultValue, // TODO: Agregue la propiedad defaultValue
        };
      });
      const distribution = {
        ...this.metadata.distribution,
        [strategyName]: metadataDistribution,
        current: strategyName
      };
      this.metadata = {
        ...this.metadata,
        distribution: cloneDeep(distribution)
      };
    } catch (e) {
      console.log(e);
    }
  }

  getRawValueBlockPeriods(): BlockPeriodDTO[] {
    const blockPeriods: BlockPeriodDTO[] = [];
    this.blockPeriods.forEach(((blockPeriod: BlockPeriodClass, index) => {
      blockPeriods.push({
        index,
        title: blockPeriod.title,
        type: blockPeriod.type,
        hasHour: blockPeriod.hasHour,
        time: blockPeriod.hasHour ? blockPeriod.time : undefined,
        foodGroupTitle: blockPeriod.foodGroupTitle,
        foodGroupId: blockPeriod.foodGroupId,
        text: blockPeriod.text,
        ingredients: this.formatIngredients(blockPeriod.ingredients),
        metadata: blockPeriod.metadata,
        mealTimeId: blockPeriod.mealPlanId
      });
    }));
    return blockPeriods;
  }

  getRawValueMealTimes(): MealTimeDTO[] {
    const mealTimes: MealTimeDTO[] = [];
    this.mealTimes.forEach(((mealTime: MealTimeClass, index) => {
      mealTimes.push({
        index,
        title: mealTime.title,
        blockPeriods: mealTime.getRawValueBlockPeriods(),
        metadata: mealTime.metadata,
        hasHour: mealTime.hasHour,
        time: mealTime.hasHour ? mealTime.time : undefined
      });
    }));
    return mealTimes;
  }

  formatIngredients(ingredientsFoodGroup: Partial<IFoodGroupIngredient>[]): IIngredient[] {
    const ingredients = [];
    ingredientsFoodGroup?.forEach((ingredient: Partial<IFoodGroupIngredient>) => {
      ingredients.push({
        _id: ingredient.ingredientId,
        measureUnitId: ingredient.measure?._id,
        units: ingredient.units,
        smae: {
          portion: ingredient.nutritionalProperties?.portions ?? ingredient.smae?.portion
        },
        category: {
          _id: ingredient.category._id,
          name: ingredient.category.name
        }
      });
    });
    return ingredients;
  }

  generateMicronutrientsTable(): void {
    this.microNutrients = [];
    for (const micro of cloneDeep(_MICRONUTRIENTS)) {
      this.microNutrients.push({
        _id: micro._id,
        name: micro.name,
        unitMeasurement: micro.unitMeasurement,
        value: +(this.nutritionalProperties[micro._id]?.toFixed(2)) ?? 0,
        ddr: 0
      });
    }
  }

  generateMacronutrients(): void {
    this.macroNutrients = [
      {
        _id: 'carbohydrate_g',
        name: 'H.C. netos',
        value: +(this.nutritionalProperties.carbohydrate_g?.toFixed(2)) ?? 0
      },
      {
        _id: 'dietaryFiber_g',
        name: 'Fibra',
        value: +(this.nutritionalProperties.dietaryFiber_g?.toFixed(2)) ?? 0
      },
      {
        _id: 'totalFat_g',
        name: 'Grasas',
        value: +(this.nutritionalProperties.totalFat_g?.toFixed(2)) ?? 0
      },
      {
        _id: 'protein_g',
        name: 'Proteínas',
        value: +(this.nutritionalProperties.protein_g?.toFixed(2)) ?? 0
      },
    ];
  }
}
